import React, { useState, useEffect, useContext } from "react";
import { useNavigate, useParams } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { PayPalScriptProvider, PayPalButtons } from "@paypal/react-paypal-js";
import { AuthContext } from "../App";
import { useCart, CART_CONTEXTS } from "../context/CartContext";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Checkbox } from "../components/ui/checkbox";
import { 
  Flower2, 
  ArrowLeft,
  Check,
  Trash2,
  Sparkles,
  Globe,
  ArrowRight,
  Loader2,
  AlertCircle
} from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const VERIFY_API = 'https://start.bloomwrightstudio.com/api';  // Verify against checkout subdomain
const PAYPAL_CLIENT_ID = process.env.REACT_APP_PAYPAL_CLIENT_ID || "test";

// Helper to normalize color_palette to array format
const getColorPaletteArray = (colorPalette) => {
  if (!colorPalette) return [];
  if (colorPalette.colors && Array.isArray(colorPalette.colors)) return colorPalette.colors;
  if (Array.isArray(colorPalette)) return colorPalette;
  return [];
};

// ============================================================
// ISOLATED COMMERCE FLOW: Create-a-Brand Activation
// This page uses CREATE_BRAND cart context (completely separate from Ready-Made)
// Uses endpoint: /api/store/activate-brand (NOT /api/store/checkout)
// ============================================================

const ActivateBrand = () => {
  const navigate = useNavigate();
  const { brandId } = useParams();
  const { user } = useContext(AuthContext);
  const { 
    clearCreateBrandCart, 
    hasOppositeContextCart,
    CART_CONTEXTS: CONTEXTS
  } = useCart();
  
  // Local state - completely isolated from Ready-Made cart
  const [brand, setBrand] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [selectedAddOns, setSelectedAddOns] = useState([]);
  const [activationReady, setActivationReady] = useState(false); // Local checkout state
  const [isCheckingOut, setIsCheckingOut] = useState(false);
  const [showContextWarning, setShowContextWarning] = useState(false);
  const [hasPurchased, setHasPurchased] = useState(false);
  const [checkingPurchase, setCheckingPurchase] = useState(true);

  // Check purchase status from checkout subdomain on page load
  useEffect(() => {
    const checkPurchaseFromCheckoutSubdomain = async () => {
      const userEmail = user?.email;
      if (!userEmail) {
        setCheckingPurchase(false);
        return;
      }
      
      try {
        const response = await fetch(`${VERIFY_API}/verify-payment?email=${encodeURIComponent(userEmail)}`);
        const data = await response.json();
        if (data.has_purchase === true) {
          setHasPurchased(true);
        }
      } catch (error) {
        console.error("Error checking purchase status from checkout subdomain:", error);
      }
      setCheckingPurchase(false);
    };
    
    checkPurchaseFromCheckoutSubdomain();
  }, [user]);

  useEffect(() => {
    if (!user) {
      // Store return URL for after login - returns to THIS page (Create Brand flow)
      sessionStorage.setItem('checkoutReturnUrl', `/activate-brand/${brandId}`);
      sessionStorage.setItem('activeCartContext', CART_CONTEXTS.CREATE_BRAND);
      navigate("/");
      return;
    }
    fetchBrand();
  }, [brandId, user]); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchBrand = async () => {
    try {
      const response = await axios.get(`${API}/brands/${brandId}`);
      const brandData = response.data;
      
      // Check if brand is already activated/purchased
      if (brandData.is_activated) {
        toast.info("This brand is already activated! Redirecting to your brand...");
        navigate(`/brand/${brandId}`);
        return;
      }
      
      // Also check purchase status via API
      try {
        const purchaseStatus = await axios.get(`${API}/brands/${brandId}/purchase-status`);
        if (purchaseStatus.data.purchased) {
          toast.info("This brand is already activated! Redirecting to your brand...");
          navigate(`/brand/${brandId}`);
          return;
        }
      } catch (purchaseError) {
        // If purchase check fails, continue to show activation page
        console.warn("Could not check purchase status:", purchaseError);
      }
      
      setBrand(brandData);
    } catch (error) {
      console.error("Error fetching brand:", error);
      toast.error("Brand not found");
      navigate("/dashboard");
    } finally {
      setIsLoading(false);
    }
  };

  const toggleAddOn = (addOnId) => {
    if (selectedAddOns.includes(addOnId)) {
      setSelectedAddOns(selectedAddOns.filter(id => id !== addOnId));
    } else {
      setSelectedAddOns([...selectedAddOns, addOnId]);
    }
  };

  const calculateTotal = () => {
    let total = 47.00; // Brand Activation price
    if (selectedAddOns.includes("formula_bundle_3")) total += 47.00;
    if (selectedAddOns.includes("formula_bundle_5")) total += 77.00;
    // Domain NOT included in Create Brand flow - only available with Ready-Made Brands
    return total;
  };

  // Isolated activation flow - does not use Ready-Made cart
  const confirmActivation = () => {
    // Check if user has items in Ready-Made cart
    if (hasOppositeContextCart(CONTEXTS.CREATE_BRAND)) {
      setShowContextWarning(true);
      return;
    }
    
    setActivationReady(true);
    toast.success("Ready to activate! Complete payment below.");
  };

  const cancelActivation = () => {
    setActivationReady(false);
    toast.info("Activation cancelled");
  };

  const handleCheckout = async () => {
    setIsCheckingOut(true);
    toast.info("PayPal checkout is being configured. Please try again later.");
    setIsCheckingOut(false);
  };

  // Show loading while checking purchase status
  if (checkingPurchase || isLoading) {
    return (
      <div className="min-h-screen bg-[#FDFCF8] flex items-center justify-center">
        <div className="text-center">
          <div className="w-8 h-8 border-4 border-[#8DA399] border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-[#78716C]">{checkingPurchase ? "Checking your access..." : "Loading..."}</p>
        </div>
      </div>
    );
  }

  // If user already purchased via checkout subdomain, show success and redirect option
  if (hasPurchased) {
    return (
      <div className="min-h-screen bg-[#FDFCF8] flex items-center justify-center">
        <div className="max-w-md mx-auto text-center px-6">
          <div className="w-20 h-20 bg-[#8DA399]/20 rounded-full flex items-center justify-center mx-auto mb-6">
            <Check className="w-10 h-10 text-[#8DA399]" />
          </div>
          <h1 className="text-3xl text-[#2C332E] mb-4" style={{ fontFamily: "'Playfair Display', serif" }}>
            You Already Have Access!
          </h1>
          <p className="text-[#78716C] mb-8">
            You've already purchased the Brand Guide. You can create and download your brand guides anytime.
          </p>
          <div className="space-y-4">
            <Button 
              onClick={() => navigate("/create")}
              className="w-full bg-[#8DA399] hover:bg-[#7A8F86] text-white rounded-full py-4"
            >
              Create New Brand
            </Button>
            <Button 
              onClick={() => navigate("/dashboard")}
              variant="outline"
              className="w-full border-[#E6E1DB] text-[#78716C] hover:bg-[#F5F0EB] rounded-full py-4"
            >
              Go to Dashboard
            </Button>
          </div>
        </div>
      </div>
    );
  }

  if (!brand) {
    return null;
  }

  return (
    <PayPalScriptProvider options={{ clientId: PAYPAL_CLIENT_ID, currency: "USD", intent: "capture" }}>
    <div className="min-h-screen bg-[#FDFCF8]">
      {/* Navigation - NO cart icon on Create-a-Brand flow */}
      <nav className="nav-glass sticky top-0 z-50 px-6 py-4">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <Button 
            variant="ghost" 
            onClick={() => navigate("/dashboard")}
            className="flex items-center gap-2 text-[#2C332E] hover:bg-[#F5F0EB]"
          >
            <ArrowLeft className="w-5 h-5" />
            Back to Dashboard
          </Button>
          <div className="flex items-center">
            <span className="text-xl font-semibold text-[#2C332E]" style={{ fontFamily: "'Playfair Display', serif" }}>
              Bloomwright Studio
            </span>
          </div>
          {/* No cart dropdown - isolated flow */}
          <div className="w-[120px]"></div>
        </div>
      </nav>

      {/* Main Content */}
      <main className="max-w-4xl mx-auto px-6 py-12">
        {/* Brand Preview */}
        <div className="mb-8">
          <Card className="bg-white rounded-2xl border-[#E6E1DB]/50 shadow-soft overflow-hidden">
            <div className="h-3 flex">
              {getColorPaletteArray(brand.color_palette).slice(0, 5).map((color, index) => (
                <div 
                  key={index}
                  className="flex-1"
                  style={{ backgroundColor: color.hex || color }}
                />
              ))}
            </div>
            <CardContent className="p-6">
              <div className="flex items-start justify-between">
                <div>
                  <h1 className="text-3xl font-semibold text-[#2C332E] mb-2">{brand.brand_name}</h1>
                  <p className="text-[#78716C] italic mb-2">&ldquo;{brand.tagline}&rdquo;</p>
                  <p className="text-sm text-[#8DA399]">{brand.business_type?.replace(/_/g, ' ')}</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Activate Your Brand Section */}
        <div className="text-center mb-8">
          <h2 className="text-3xl font-semibold text-[#2C332E] mb-3">Activate Your Brand</h2>
          <p className="text-[#78716C] max-w-xl mx-auto">
            You&apos;ve created your brand concept. Activate it to unlock full access and begin working with it.
          </p>
        </div>

        {/* Core Item - Brand Activation */}
        <div className="mb-8">
          <div className="flex items-center gap-2 mb-4">
            <span className="text-xl">🌿</span>
            <h3 className="text-lg font-semibold text-[#2C332E]">CORE ITEM (Required)</h3>
          </div>
          
          <Card className="bg-gradient-to-br from-[#8DA399]/5 to-[#C77D63]/5 rounded-2xl border-2 border-[#8DA399]">
            <CardContent className="p-6">
              <div className="flex items-start justify-between mb-4">
                <div>
                  <h4 className="text-xl font-semibold text-[#2C332E]">Brand Activation</h4>
                  <p className="text-[#C77D63] font-bold text-lg">$47 — one-time</p>
                </div>
                <div className="w-10 h-10 rounded-full bg-[#8DA399] flex items-center justify-center">
                  <Check className="w-5 h-5 text-white" />
                </div>
              </div>
              <p className="text-[#78716C] mb-3">
                Unlock full access to your brand, including its visual identity, brand story, business guide, packaging recommendations, and starter formula.
              </p>
              <p className="text-xs text-[#8DA399]">
                This activates one brand concept. You can always create or explore additional brands later.
              </p>
            </CardContent>
          </Card>
        </div>

        {/* Optional Add-Ons */}
        <div className="mb-8">
          <div className="flex items-center gap-2 mb-2">
            <span className="text-xl">➕</span>
            <h3 className="text-lg font-semibold text-[#2C332E]">OPTIONAL NEXT STEPS</h3>
          </div>
          <p className="text-sm text-[#78716C] mb-6">
            These options support your next steps. You can add them now or return to them later.
          </p>

          <div className="space-y-4">
            {/* Formula Options - Domain NOT included in Create Brand flow */}
            <div className="flex items-center gap-2 mb-3">
              <span>🧪</span>
              <h4 className="font-medium text-[#2C332E]">Formula Options</h4>
            </div>

            <Card 
              className={`rounded-xl border-2 cursor-pointer transition-all duration-200 ${
                selectedAddOns.includes("formula_bundle_3")
                  ? 'border-[#8DA399] bg-[#8DA399]/5'
                  : 'border-[#E6E1DB] hover:border-[#8DA399]/50'
              }`}
              onClick={() => toggleAddOn("formula_bundle_3")}
            >
              <CardContent className="p-5">
                <div className="flex items-start gap-4">
                  <Checkbox checked={selectedAddOns.includes("formula_bundle_3")} className="mt-1" />
                  <div className="flex-1">
                    <div className="flex items-center justify-between mb-1">
                      <h4 className="font-semibold text-[#2C332E]">Additional Formula Pack (3 Formulas)</h4>
                      <span className="text-[#C77D63] font-bold">$47</span>
                    </div>
                    <p className="text-sm text-[#78716C] mb-2">
                      Add three additional formulas to explore structure, texture, and consistency at your own pace.
                    </p>
                    <p className="text-xs text-[#8DA399]">Perfect for founders who want variety while learning.</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card 
              className={`rounded-xl border-2 cursor-pointer transition-all duration-200 ${
                selectedAddOns.includes("formula_bundle_5")
                  ? 'border-[#8DA399] bg-[#8DA399]/5'
                  : 'border-[#E6E1DB] hover:border-[#8DA399]/50'
              }`}
              onClick={() => toggleAddOn("formula_bundle_5")}
            >
              <CardContent className="p-5">
                <div className="flex items-start gap-4">
                  <Checkbox checked={selectedAddOns.includes("formula_bundle_5")} className="mt-1" />
                  <div className="flex-1">
                    <div className="flex items-center justify-between mb-1">
                      <h4 className="font-semibold text-[#2C332E]">Advanced Formula Pack (5 Formulas)</h4>
                      <span className="text-[#C77D63] font-bold">$77</span>
                    </div>
                    <p className="text-sm text-[#78716C] mb-2">
                      A deeper formula set for founders ready to explore more advanced formulation techniques.
                    </p>
                    <p className="text-xs text-[#8DA399]">Best suited for those with prior formulation experience.</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>

        {/* Activation Summary */}
        <Card className="bg-[#F5F0EB] rounded-2xl border-0 shadow-soft">
          <CardContent className="p-8">
            <div className="flex items-center gap-2 mb-2">
              <span>🧾</span>
              <h3 className="text-lg font-semibold text-[#2C332E]">ACTIVATION SUMMARY</h3>
            </div>
            <p className="text-xs text-[#8DA399] mb-4">Review your activation details before unlocking your brand.</p>
            
            <div className="bg-white rounded-xl p-4 mb-6">
              <p className="font-medium text-[#2C332E] mb-3">This Activation Includes</p>
              
              {/* Brand Activation - Core Item */}
              <div className="py-3 border-b border-[#E6E1DB]">
                <div className="flex justify-between items-start">
                  <div>
                    <span className="text-[#2C332E] font-medium">🌿 Brand Activation — {brand.brand_name}</span>
                    <p className="text-xs text-[#78716C] mt-1">One-time unlock of your saved brand concept</p>
                  </div>
                  <span className="text-[#2C332E] font-medium">$47.00</span>
                </div>
              </div>

              {/* Optional Add-ons - Only show if any selected */}
              {selectedAddOns.length > 0 && (
                <div className="py-3 border-b border-[#E6E1DB]">
                  <p className="text-xs text-[#78716C] mb-2">Optional add-ons:</p>
                  {selectedAddOns.includes("formula_bundle_3") && (
                    <div className="flex justify-between py-1">
                      <span className="text-sm text-[#78716C]">🧪 Additional Formula Pack (3)</span>
                      <span className="text-sm text-[#2C332E]">$47.00</span>
                    </div>
                  )}
                  {selectedAddOns.includes("formula_bundle_5") && (
                    <div className="flex justify-between py-1">
                      <span className="text-sm text-[#78716C]">🧪 Advanced Formula Pack (5)</span>
                      <span className="text-sm text-[#2C332E]">$77.00</span>
                    </div>
                  )}
                </div>
              )}
              
              {/* Total */}
              <div className="flex justify-between pt-4 mt-2">
                <span className="font-semibold text-[#2C332E]">Activation Total</span>
                <span className="text-2xl font-bold text-[#2C332E]">${calculateTotal().toFixed(2)}</span>
              </div>
            </div>

            {/* Pricing Clarity - Between Total and Button */}
            <p className="text-sm text-[#78716C] text-center my-4">
              This is a one-time activation to unlock your brand concept.
            </p>

            {/* Confirm & Activate / Checkout - ISOLATED FLOW */}
            {!activationReady ? (
              <div className="space-y-3">
                <Button 
                  onClick={confirmActivation}
                  className="bg-[#8DA399] hover:bg-[#7A8F86] text-white rounded-full px-8 py-6 text-lg hover-lift w-full flex items-center justify-center gap-2"
                >
                  <Sparkles className="w-5 h-5" />
                  Confirm & Activate
                </Button>
              </div>
            ) : (
              <div className="space-y-4">
                <div className="bg-white rounded-xl p-4 border-2 border-[#8DA399]">
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center gap-2">
                      <Check className="w-5 h-5 text-[#8DA399]" />
                      <span className="font-semibold text-[#2C332E]">Ready to Activate</span>
                    </div>
                    <Button 
                      variant="ghost" 
                      size="sm"
                      onClick={cancelActivation}
                      className="text-red-500 hover:text-red-700 hover:bg-red-50 p-2"
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                  <div className="flex justify-between items-center">
                    <div>
                      <p className="text-[#2C332E] font-medium">{brand.brand_name} Brand Activation</p>
                      <p className="text-sm text-[#78716C]">
                        {selectedAddOns.length > 0 && `${selectedAddOns.length} add-on${selectedAddOns.length > 1 ? 's' : ''}`}
                      </p>
                    </div>
                    <p className="font-bold text-[#2C332E]">${calculateTotal().toFixed(2)}</p>
                  </div>
                </div>

                {PAYPAL_CLIENT_ID && PAYPAL_CLIENT_ID !== "test" && PAYPAL_CLIENT_ID !== "YOUR_PAYPAL_CLIENT_ID" ? (
                  <div className="paypal-button-container" data-testid="paypal-buttons">
                    {/* Payment Method Info */}
                    <div className="text-center mb-4">
                      <p className="text-sm font-medium text-[#2C332E]">Payment Method</p>
                      <p className="text-xs text-[#78716C]">Secure checkout via PayPal.</p>
                      <p className="text-xs text-[#8DA399]">Complete your purchase using your PayPal balance, card, or linked bank account.</p>
                    </div>
                    <PayPalButtons
                        style={{ 
                          layout: "vertical", 
                          color: "gold", 
                          shape: "pill", 
                          label: "paypal",
                          height: 50
                        }}
                        forceReRender={[calculateTotal(), selectedAddOns]}
                        createOrder={async () => {
                          try {
                            const response = await axios.post(`${API}/store/activate-brand`, {
                              brand_id: brand.brand_id,
                              add_ons: selectedAddOns
                            });
                            return response.data.order_id;
                          } catch (error) {
                            console.error("PayPal order creation error:", error);
                            toast.error("Failed to create payment. Please try again.");
                            throw error;
                          }
                        }}
                        onApprove={async (data) => {
                          try {
                            const response = await axios.post(`${API}/store/capture-payment/${data.orderID}`);
                            if (response.data.status === "COMPLETED") {
                              toast.success("Brand activated successfully!");
                              clearCreateBrandCart(); // Clear ONLY Create Brand cart
                              navigate(`/brand/${brand.brand_id}`);
                            } else {
                              toast.info("Payment is being processed...");
                            }
                          } catch (error) {
                            console.error("Payment capture error:", error);
                            toast.error("Payment processing failed. Please contact support.");
                          }
                        }}
                        onError={(err) => {
                          console.error("PayPal error:", err);
                          toast.error("Payment failed. Please try again.");
                        }}
                        onCancel={() => toast.info("Payment cancelled.")}
                      />
                    <p className="text-xs text-[#A8A29E] text-center mt-2">
                      Secure checkout powered by PayPal.
                    </p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    <Button 
                      onClick={handleCheckout}
                      disabled={isCheckingOut}
                      className="bg-[#0070BA] hover:bg-[#003087] text-white rounded-full px-8 py-6 text-lg hover-lift w-full flex items-center justify-center gap-2"
                    >
                      {isCheckingOut ? (
                        <>
                          <Loader2 className="w-5 h-5 animate-spin" />
                          Processing...
                        </>
                      ) : (
                        <>
                          <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor">
                            <path d="M7.076 21.337H2.47a.641.641 0 0 1-.633-.74L4.944.901C5.026.382 5.474 0 5.998 0h7.46c2.57 0 4.578.543 5.69 1.81 1.01 1.15 1.304 2.42 1.012 4.287-.023.143-.047.288-.077.437-.983 5.05-4.349 6.797-8.647 6.797h-2.19c-.524 0-.968.382-1.05.9l-1.12 7.106zm14.146-14.42a3.35 3.35 0 0 0-.607-.541c-.013.076-.026.175-.041.254-.59 3.025-2.566 6.082-8.558 6.082H9.83l-1.694 10.727h4.39a.563.563 0 0 0 .556-.479l.023-.118.442-2.8.028-.154a.563.563 0 0 1 .556-.479h.35c2.266 0 4.04-.92 4.559-3.58.216-1.112.104-2.04-.359-2.69a2.23 2.23 0 0 0-.559-.522z"/>
                          </svg>
                          Checkout with PayPal
                        </>
                      )}
                    </Button>
                    <p className="text-xs text-amber-600 text-center bg-amber-50 rounded-lg p-2">
                      ⚠️ PayPal checkout is being configured. Please try again later or contact support.
                    </p>
                  </div>
                )}
              </div>
            )}

            {/* Reassurance Footer */}
            <div className="mt-6 pt-6 border-t border-[#E6E1DB]/50 text-center">
              <p className="text-sm text-[#78716C] italic">
                🌱 You don&apos;t need to have everything figured out today.<br />
                This is simply the next gentle step forward.
              </p>
            </div>
          </CardContent>
        </Card>
      </main>

      {/* Context Warning Modal - shown when user tries to activate while having Ready-Made cart items */}
      {showContextWarning && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl p-6 max-w-md w-full shadow-xl">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 bg-amber-100 rounded-full flex items-center justify-center">
                <AlertCircle className="w-5 h-5 text-amber-600" />
              </div>
              <h3 className="text-lg font-semibold text-[#2C332E]">Active Cart Detected</h3>
            </div>
            <p className="text-[#78716C] mb-6">
              You have items in your Ready-Made Brand cart. You can only have one active cart at a time.
              Please complete or clear that cart before activating your brand.
            </p>
            <div className="flex gap-3">
              <Button
                variant="outline"
                onClick={() => setShowContextWarning(false)}
                className="flex-1 rounded-full"
              >
                Cancel
              </Button>
              <Button
                onClick={() => {
                  setShowContextWarning(false);
                  navigate('/store');
                }}
                className="flex-1 bg-[#C77D63] hover:bg-[#B06A52] text-white rounded-full"
              >
                Go to Store
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
    </PayPalScriptProvider>
  );
};

export default ActivateBrand;
